import { DataTable } from '@/components/table/DataTable';
import { FlexCol } from '@/components/ui/flex';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useKnowledgeGroupsService,
  useSourcesWithRefetchService,
} from '@/services/sources';
import { SupportedRegion } from '@ditto/shared';
import { getPaginationRowModel } from '@tanstack/react-table';
import { useMemo, useState, type FC } from 'react';
import { SourcesFilters } from './components/SourcesFilters';
import { useSourcesTableColumns } from './components/SourcesTableColumns';
export const SourcesPage: FC = () => {
  const {
    sources,
    isLoading: sourcesLoading,
    refetch,
    processingFilesPromise,
    startTrackingFiles,
  } = useSourcesWithRefetchService();
  const { knowledgeGroups, isLoading: groupsLoading } =
    useKnowledgeGroupsService();

  const [searchString, setSearchString] = useState<string>('');
  const [selectedKnowledgeGroups, setSelectedKnowledgeGroups] = useState<
    string[]
  >([]);
  const [selectedRegions, setSelectedRegions] = useState<SupportedRegion[]>([]);
  const [selectedSourceTypes, setSelectedSourceTypes] = useState<string[]>([]);

  const debouncedSearchString = useDebounce(searchString, 300);

  // Apply filters
  const filteredSources = useMemo(() => {
    let filtered = sources.filter(
      source =>
        (source.sourceType === 'UserFile' && source.status === 'completed') ||
        source.sourceType === 'SharedKnowledgeFile'
    );

    // Search filter
    if (debouncedSearchString) {
      const searchLower = debouncedSearchString.toLowerCase();
      filtered = filtered.filter((source: any) =>
        source.fileName.toLowerCase().includes(searchLower)
      );
    }

    // Knowledge group filter
    if (selectedKnowledgeGroups.length > 0) {
      filtered = filtered.filter((source: any) => {
        if (!source.groupId) {
          return false;
        }
        return selectedKnowledgeGroups.includes(source.groupId);
      });
    }

    // Regulatory region filter
    if (selectedRegions.length > 0) {
      filtered = filtered.filter((source: any) => {
        if (!source.regions || source.regions.length === 0) {
          return false;
        }
        return selectedRegions.some(region => source.regions?.includes(region));
      });
    }

    // Source type filter
    if (selectedSourceTypes.length > 0) {
      // If "all" is selected, don't filter (show all sources)
      if (!selectedSourceTypes.includes('all')) {
        filtered = filtered.filter((source: any) =>
          selectedSourceTypes.includes(source.sourceType)
        );
      }
    }

    return filtered;
  }, [
    sources,
    debouncedSearchString,
    selectedKnowledgeGroups,
    selectedRegions,
    selectedSourceTypes,
  ]);

  const columns = useSourcesTableColumns(knowledgeGroups);

  const isLoading = sourcesLoading || groupsLoading;

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  return (
    <FlexCol p="6" h="full" w="full" gap="6" className="max-w-[1400px]">
      <FlexCol gap="2">
        <h1 className="text-3xl font-semibold">Knowledge Base</h1>
        <p className="text-neutral-600">
          Browse the documents powering your AI. Add your own documents to
          improve your AI's responses further.
        </p>
      </FlexCol>
      <SourcesFilters
        searchString={searchString}
        setSearchString={setSearchString}
        selectedKnowledgeGroups={selectedKnowledgeGroups}
        setSelectedKnowledgeGroups={setSelectedKnowledgeGroups}
        onUploadComplete={async uploadedFileIds => {
          startTrackingFiles(uploadedFileIds);
          await refetch();
          await processingFilesPromise(uploadedFileIds);
        }}
        selectedRegions={selectedRegions}
        setSelectedRegions={setSelectedRegions}
        selectedSourceTypes={selectedSourceTypes}
        setSelectedSourceTypes={setSelectedSourceTypes}
      />

      <DataTable
        columns={columns}
        data={filteredSources}
        isLoading={isLoading}
        options={{
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: setPagination,
          state: { pagination },
        }}
      />
    </FlexCol>
  );
};
